/**
 * CallbackAttemptTimeline — visual recap of a callback's lifecycle
 * (Story 7.13b AC3). The DB does not store individual attempt rows
 * (Story 4.2 keeps only the rolling counters: `attempt_count`, `last_error`,
 * `last_attempted_at`, `executed_at`, `cancelled`). The timeline therefore
 * renders the **discrete state transitions we know about**:
 *
 *   1. Scheduled at  scheduledAt
 *   2. Last attempt  lastAttemptedAt   (when attempt_count > 0)
 *   3. Final state   executedAt | cancelled | failed-with-error | expired
 *
 * Steps without a known timestamp are dimmed — they make the lifecycle
 * legible at a glance even before the final state is reached.
 */
import { type CallbackExecutionStatus } from '@/lib/callbacks';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';

export interface CallbackAttemptTimelineProps {
  scheduledAt: string;
  lastAttemptedAt: string | null;
  executedAt: string | null;
  cancelled: boolean;
  cancelledReason: string | null;
  attemptCount: number;
  lastError: string | null;
  status: CallbackExecutionStatus;
}

interface TimelineEntry {
  id: string;
  label: string;
  at: string | null;
  detail?: string;
  tone: 'pending' | 'info' | 'success' | 'error' | 'muted';
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

function buildEntries(props: CallbackAttemptTimelineProps): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    {
      id: 'scheduled',
      label: 'Agendado',
      at: props.scheduledAt,
      tone: 'info',
    },
  ];

  if (props.attemptCount > 0 || props.lastAttemptedAt) {
    entries.push({
      id: 'attempts',
      label: props.attemptCount === 1 ? '1 tentativa' : `${props.attemptCount} tentativas`,
      at: props.lastAttemptedAt,
      detail: props.lastError ?? undefined,
      tone: props.lastError ? 'error' : 'pending',
    });
  }

  if (props.executedAt) {
    entries.push({
      id: 'executed',
      label: 'Executado',
      at: props.executedAt,
      tone: 'success',
    });
  } else if (props.cancelled) {
    entries.push({
      id: 'cancelled',
      label: 'Cancelado',
      at: null,
      detail: props.cancelledReason ?? undefined,
      tone: 'muted',
    });
  } else if (props.status === 'failed') {
    entries.push({
      id: 'failed',
      label: 'Falhou após retries',
      at: null,
      detail: props.lastError ?? undefined,
      tone: 'error',
    });
  } else if (props.status === 'expired') {
    entries.push({
      id: 'expired',
      label: 'Expirou (24h Meta window)',
      at: null,
      tone: 'muted',
    });
  }

  return entries;
}

const TONE_CLASSES: Record<TimelineEntry['tone'], string> = {
  pending: 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-300',
  info: 'border-slate-400 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  success: 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-300',
  error: 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300',
  muted: 'border-muted-foreground/30 bg-muted text-muted-foreground',
};

export function CallbackAttemptTimeline(props: CallbackAttemptTimelineProps) {
  const entries = buildEntries(props);

  return (
    <ol
      className="relative space-y-4 border-l border-border pl-4"
      data-testid="callback-attempt-timeline"
    >
      {entries.map((entry) => (
        <li key={entry.id} className="relative">
          <span
            aria-hidden
            className={`absolute -left-[1.4rem] top-1.5 inline-flex h-3 w-3 items-center justify-center rounded-full border-2 ${
              TONE_CLASSES[entry.tone]
            }`}
          />
          <div className="text-sm">
            <span className="font-medium">{entry.label}</span>
            <span className="ml-2 text-xs text-muted-foreground">{formatDateTime(entry.at)}</span>
          </div>
          {entry.detail ? (
            <p className="mt-1 max-w-xl truncate text-xs text-muted-foreground">{entry.detail}</p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
